import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, istDateTime } from "@/lib/api";

const TOGGLES = [
  ["enabled", "All email alerts"], ["critical", "Critical alarms: kill switch, daily stop, mismatch, budget hit"],
  ["daily", "Daily P&L summary · 15:45 IST"], ["weekly", "Weekly recap · Fri 16:00 IST"], ["monthly", "Monthly live statement · last trading day"], ["include_dry", "Include dry-run alarms"],
];
const SENDS = [["test", "Test"], ["daily", "Daily now"], ["weekly", "Weekly now"], ["kill_test", "Kill alert sample"]];

export const AlertsPanel = () => {
  const [c, setC] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(null);
  const loadLog = () => api.get("/alerts/log").then((r) => setLog(r.data));
  useEffect(() => { api.get("/alerts/config").then((r) => setC(r.data)); loadLog(); }, []);
  if (!c) return null;

  const save = async () => {
    try { setC((await api.put("/alerts/config", c)).data); toast.success("Alert settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (k) => {
    setBusy(k);
    try { const r = await api.post(`/alerts/send/${k}`); toast.success(`Sent: ${r.data.subject}`); } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); loadLog(); }
  };

  return (
    <div className="panel p-4" data-testid="panel-email-alerts">
      <div className="eyebrow flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Email alerts</div>
      <label className="mt-3 block"><span className="eyebrow !text-[10px]">Send alerts to</span>
        <Input data-testid="input-alert-email" type="email" placeholder="you@example.com" className="mt-1 h-8 bg-slate-900 text-xs"
          value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></label>
      <div className="mt-3 space-y-2">
        {TOGGLES.map(([k, t]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-slate-300">
            <Switch data-testid={`switch-alert-${k}`} checked={!!c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} /> {t}
          </label>
        ))}
      </div>
      <Button data-testid="button-save-alerts" onClick={save} className="mt-3 w-full bg-indigo-500 text-white hover:bg-indigo-400">Save alerts</Button>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {SENDS.map(([k, t]) => (
          <Button key={k} data-testid={`button-send-${k}`} variant="outline" size="sm" disabled={!!busy || !c.email} onClick={() => send(k)} className="border-slate-700 text-xs">
            {busy === k ? "Sending…" : t}
          </Button>
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-slate-500">Sent as "{c.from_name}" from the platform's verified mail domain. Repeat alarms are limited to one every 15 min; kill-switch and daily-stop alerts always go out and list your open positions.</p>
      {log.length > 0 && (
        <div className="mt-3 max-h-48 space-y-1 overflow-y-auto" data-testid="alert-log">
          {log.map((r) => (
            <div key={r.id} className="border-t border-slate-800 pt-1 text-[11px]" data-testid="alert-log-row">
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 truncate text-slate-300" title={r.subject}>{r.subject}</span>
                <span className={`shrink-0 num ${r.status === "sent" ? "up" : "down"}`}>{r.status} · {istDateTime(r.ts)}</span>
              </div>
              {r.error && <div className="text-[10px] text-slate-500" data-testid="alert-log-error">{r.error}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
